import {editorValueSignature} from './native-envelope';
import {readCompanyContinueClip, type CompanyContinueClip} from './continue-clip';

export const VIDEO_MOVEMENTS = [
  '자연스럽고 부드러운 움직임',
  '활기차고 역동적인 움직임',
  '우아하고 절제된 움직임',
  '경쾌하고 장난스러운 움직임',
  '극적이고 긴장감 있는 움직임',
] as const;
export type VideoDraftSource =
  | {type:'image';assetId:string}
  | {type:'cut';clipId:string;frame:'end'}
  | {type:'cut';clipId:string;frame:'playhead';playheadFrame:number};
export type VideoDraftInput = {source:VideoDraftSource;movement:number;customPrompt:string;from:number;fps:number;expectedSourceSignature?:string;endAssetId?:string;expectedEndSignature?:string};
export type CompanyVideoDraft = {
  source:VideoDraftSource;assetId:string;remoteUrl:string;sourceType:'image'|'video';
  endAssetId?:string;endFrame?:{assetId:string;remoteUrl:string;signature:string};
  sourceFrameSeconds:number|null;continuation:CompanyContinueClip|null;
  movement:number;customPrompt:string;request:string;from:number;fps:number;
  documentSignature:string;sourceSignature:string;aspectRatio:'9:16'|'16:9';
};
const record=(v:unknown):v is Record<string,unknown>=>!!v&&typeof v==='object'&&!Array.isArray(v);
const frame=(v:unknown):v is number=>typeof v==='number'&&Number.isSafeInteger(v)&&v>=0&&v<=10_000_000;
const id=(v:unknown):v is string=>typeof v==='string'&&v.length>0&&v.length<=512;
function boundedVideoSample(start:number,sample:number,duration:number,fps:number){
  if(![start,sample,duration].every(Number.isFinite)||start<0||start>=duration||sample<start||sample>duration+1/fps)
    throw new Error('선택한 프레임이 원본 재생 구간을 벗어났습니다. 컷을 조정해 주세요.');
  // The existing clip reader permits one timeline frame of duration rounding.
  // Both preview and host extraction receive the same interior sample for that tail.
  return Math.max(start,Math.min(sample,duration-Math.min(0.000001,duration/2)));
}

/** Resolve identifiers against the captured work; never accept a client-supplied media URL. */
export function readCompanyVideoDraft(value:unknown,document:unknown):CompanyVideoDraft {
  if(!record(value)||!record(value.source)||!record(document)||!record(document.assets)||!record(document.items)
    ||!frame(value.from)||typeof value.fps!=='number'||!Number.isFinite(value.fps)||value.fps<=0||value.fps!==document.fps
    ||typeof value.movement!=='number'||!Number.isInteger(value.movement)||value.movement<0||value.movement>=VIDEO_MOVEMENTS.length
    ||typeof value.customPrompt!=='string'||value.customPrompt.length>2000
    ||typeof document.compositionWidth!=='number'||!Number.isFinite(document.compositionWidth)||document.compositionWidth<=0
    ||typeof document.compositionHeight!=='number'||!Number.isFinite(document.compositionHeight)||document.compositionHeight<=0)
    throw new Error('이미지·움직임·요청 내용을 확인해 주세요.');
  const input=value.source;
  let source:VideoDraftSource,assetId:string,remoteUrl:string,sourceType:'image'|'video',sourceFrameSeconds:number|null=null;
  let continuation:CompanyContinueClip|null=null;
  if(input.type==='image'&&id(input.assetId)){
    const asset=document.assets[input.assetId];
    if(!record(asset)||asset.id!==input.assetId||asset.type!=='image'||typeof asset.remoteUrl!=='string')
      throw new Error('현재 작업에 업로드된 이미지 원본을 선택해 주세요.');
    source={type:'image',assetId:input.assetId};assetId=input.assetId;remoteUrl=asset.remoteUrl;sourceType='image';
  }else if(input.type==='cut'&&id(input.clipId)&&(input.frame==='end'||input.frame==='playhead')){
    continuation=readCompanyContinueClip(document,input.clipId);
    assetId=continuation.assetId;remoteUrl=continuation.remoteUrl;sourceType=continuation.type;
    if(input.frame==='playhead'){
      const item=document.items[input.clipId];
      if(!record(item)||!frame(input.playheadFrame)||typeof item.from!=='number'||typeof item.durationInFrames!=='number'
        ||input.playheadFrame<item.from||input.playheadFrame>=item.from+item.durationInFrames)
        throw new Error('선택한 컷 안으로 재생 위치를 옮겨 주세요.');
      source={type:'cut',clipId:input.clipId,frame:'playhead',playheadFrame:input.playheadFrame};
      if(sourceType==='video'){
        // readCompanyContinueClip already validated this source window and playback rate.
        const asset=document.assets[assetId];if(!record(asset))throw new Error('원본 영상을 확인해 주세요.');
        const start=Number(item.videoStartFromInSeconds);
        sourceFrameSeconds=boundedVideoSample(start,start+(input.playheadFrame-item.from)/value.fps*Number(item.playbackRate),Number(asset.durationInSeconds),value.fps);
      }
    }else{
      source={type:'cut',clipId:input.clipId,frame:'end'};
      if(sourceType==='video'){
        const item=document.items[input.clipId];
        const asset=document.assets[assetId];
        if(!record(item)||!record(asset))throw new Error('선택한 원본을 확인해 주세요.');
        const start=Number(item.videoStartFromInSeconds);
        const last=start+(Number(item.durationInFrames)-1)/value.fps*Number(item.playbackRate);
        sourceFrameSeconds=boundedVideoSample(start,last,Number(asset.durationInSeconds),value.fps);
      }
    }
  }else throw new Error('생성에 사용할 이미지나 컷을 선택해 주세요.');
  const url=new URL(remoteUrl);
  if(url.protocol!=='https:'||url.username||url.password)throw new Error('업로드된 원본 주소가 필요합니다.');
  let endFrame:CompanyVideoDraft['endFrame'];
  if(value.endAssetId!==undefined){
    if(!id(value.endAssetId))throw Error('종료 이미지를 다시 선택해 주세요.');
    const asset=document.assets[value.endAssetId];
    if(!record(asset)||asset.id!==value.endAssetId||asset.type!=='image'||typeof asset.remoteUrl!=='string')throw Error('현재 작업에 업로드된 종료 이미지를 선택해 주세요.');
    const endUrl=new URL(asset.remoteUrl);if(endUrl.protocol!=='https:'||endUrl.username||endUrl.password)throw Error('종료 이미지를 자료함에 업로드한 뒤 선택해 주세요.');
    endFrame={assetId:value.endAssetId,remoteUrl:endUrl.href,signature:editorValueSignature(asset)};
    if(value.expectedEndSignature!==undefined&&value.expectedEndSignature!==endFrame.signature)throw Error('종료 이미지가 바뀌었습니다. 다시 선택해 주세요.');
  }else if(value.expectedEndSignature!==undefined)throw Error('종료 이미지를 다시 선택해 주세요.');
  const customPrompt=value.customPrompt.trim();
  const sourceSignature=editorValueSignature({source,asset:document.assets[assetId],continuation});
  if(value.expectedSourceSignature!==undefined&&value.expectedSourceSignature!==sourceSignature)
    throw new Error('선택한 원본이나 컷이 바뀌었습니다. 원본을 다시 선택해 주세요.');
  return {source,assetId,remoteUrl,sourceType,sourceFrameSeconds,continuation,...(endFrame?{endAssetId:endFrame.assetId,endFrame}:{}),movement:value.movement,customPrompt,
    request:[VIDEO_MOVEMENTS[value.movement],customPrompt].filter(Boolean).join('\n'),from:value.from,fps:value.fps,
    documentSignature:editorValueSignature(document),sourceSignature,
    aspectRatio:document.compositionHeight>=document.compositionWidth?'9:16':'16:9'};
}
